import { Alert, Badge, Card, Col, ListGroup, Row, Spinner } from "react-bootstrap";
import LoggedInLayout from "../../layouts/loggedin-layout";
import { useEffect, useMemo, useState } from "react";
import styles from "../../styles/Reports.module.css";
import ReportView from "./reportview";
import { useReportsCatalogQuery } from "../../new/reports.query";

const ViewReports = () => {
    const { data, isLoading, isError } = useReportsCatalogQuery();
    const [selectedReportCategoryID, setSelectedReportCategoryID] = useState(null);
    const [selectedReport, setSelectedReport] = useState(null);
    const reportCategories = useMemo(() => data?.categories ?? [], [data?.categories]);

    useEffect(() => {
        document.title = "MFP - Reports";
    }, []);

    useEffect(() => {
        if (reportCategories.length === 0) {
            return;
        }

        const selectedCategoryExists = reportCategories.some((category) => category.id === selectedReportCategoryID);

        if (!selectedCategoryExists) {
            setSelectedReportCategoryID(reportCategories[0].id);
        }
    }, [reportCategories, selectedReportCategoryID]);

    const selectedCategory = useMemo(() => {
        return reportCategories.find((category) => category.id === selectedReportCategoryID);
    }, [reportCategories, selectedReportCategoryID]);

    const handleCategorySelect = (categoryId) => {
        setSelectedReportCategoryID(categoryId);
        setSelectedReport(null);
    };

    return (
        <LoggedInLayout>
            <div className={styles.reportsShell}>
                <Row className="g-3">
                    <Col md={3} xl={2}>
                        <Card className={styles.reportCategoryCard}>
                            <Card.Header>
                                <h5 className="text-center card-title mb-0">Report Categories</h5>
                            </Card.Header>
                            <ListGroup variant="flush">
                                {isLoading && (
                                    <div className={styles.reportCategoryLoading}>
                                        <Spinner animation="border" size="sm" />
                                        <span>Loading</span>
                                    </div>
                                )}
                                {!isLoading && reportCategories.map((category) => (
                                    <ListGroup.Item
                                        key={category.id}
                                        action
                                        onClick={() => handleCategorySelect(category.id)}
                                        active={category.id === selectedReportCategoryID}
                                        className={styles.reportCategoryItem}
                                        type="button"
                                    >
                                        <span>{category.name}</span>
                                        <Badge bg="light" text="dark">{category.reports?.length ?? 0}</Badge>
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Card>
                    </Col>
                    <Col md={9} xl={10} className={styles.reportArea}>
                        {isError && (
                            <Alert variant="danger" className="mb-0">
                                Reports could not be loaded.
                            </Alert>
                        )}
                        {!isError && selectedReport && (
                            <ReportView
                                key={selectedReport.id}
                                report={selectedReport}
                                returnFunction={() => setSelectedReport(null)}
                            />
                        )}
                        {!isError && !selectedReport && selectedCategory && (
                            <div className={styles.reportList}>
                                <div className={styles.reportListHeader}>
                                    <div>
                                        <h4 className={styles.reportListTitle}>{selectedCategory.name}</h4>
                                        <p>{selectedCategory.description}</p>
                                    </div>
                                    <Badge bg="secondary">{selectedCategory.reports?.length ?? 0} reports</Badge>
                                </div>
                                <Row className="g-3">
                                    {(selectedCategory.reports ?? []).map((report) => (
                                        <Col lg={4} md={6} sm={12} key={report.id}>
                                            <button
                                                className={`${styles.reportCard} semanticBtnOverride`}
                                                type="button"
                                                aria-label={`View report titled ${report.title}`}
                                                onClick={() => setSelectedReport(report)}
                                            >
                                                <span className={styles.reportCardCode}>{report.id}</span>
                                                <strong>{report.title}</strong>
                                                <span className={styles.reportDescription}>
                                                    {report.description}
                                                </span>
                                            </button>
                                        </Col>
                                    ))}
                                </Row>
                            </div>
                        )}
                        {!isLoading && !isError && !selectedCategory && (
                            <div className={styles.noCategorySelected}>
                                No report categories are available.
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </LoggedInLayout>
    );
};

export default ViewReports;
